import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import './MessageInputEditor.css';
import { expandTags, parseTags } from '../utils/tags';

/**
 * Reusable message input editor with shared background footer buttons.
 * Props:
 * - value: string
 * - onChange: (string) => void
 * - onSend: (content: string) => Promise|void
 * - disabled?: boolean
 * - placeholder?: string
 * - providers?: Record<string,{fetch:Function, parse?:Function}>
 * - autoExpandTags?: boolean (default false) – if true, expand tags before sending
 * - onExpandError?: (err: Error) => void
 * - footerButtonsRight?: ReactNode – additional buttons on the right side of footer
 * - footerButtonsLeft?: ReactNode – additional buttons on the left side of footer
 * - belowFooter?: ReactNode – full-width strip under the footer (e.g. project workflow buttons)
 */
export default function MessageInputEditor({
  value,
  onChange,
  onSend,
  disabled = false,
  placeholder = 'Type a message…',
  providers = {},
  autoExpandTags = false,
  onExpandError,
  footerButtonsRight,
  footerButtonsLeft,
  belowFooter = null,
}) {
  const taRef = useRef(null);

  const syncTextareaHeight = useCallback(() => {
    const ta = taRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    const styles = window.getComputedStyle(ta);
    const lineHeight = parseFloat(styles.lineHeight) || (parseFloat(styles.fontSize) || 16) * 1.45;
    const padY = (parseFloat(styles.paddingTop) || 0) + (parseFloat(styles.paddingBottom) || 0);
    const minPx = Math.ceil(lineHeight * 3 + padY);
    const computedMax = parseFloat(styles.maxHeight);
    const maxPx = Number.isFinite(computedMax) && computedMax > 0 ? computedMax : 800;
    const next = Math.min(Math.max(ta.scrollHeight, minPx), maxPx);
    ta.style.height = `${next}px`;
  }, []);

  useEffect(() => {
    syncTextareaHeight();
  }, [value, syncTextareaHeight]);

  useEffect(() => {
    const onResize = () => syncTextareaHeight();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [syncTextareaHeight]);

  const hasText = (value || '').trim().length > 0;

  const handleSend = useCallback(async () => {
    if (!hasText || disabled) return;
    let content = value;
    if (autoExpandTags) {
      try {
        content = await expandTags(content, providers);
      } catch (e) {
        if (onExpandError) onExpandError(e);
      }
    }
    await Promise.resolve(onSend?.(content));
  }, [hasText, disabled, value, autoExpandTags, providers, onSend, onExpandError]);

  // insertAtCursor removed with tag button

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }, [handleSend]);

  const foundTags = useMemo(() => parseTags(value || ''), [value]);

  return (
    <div className="message-input">
      <div className="message-input__editor">
        <textarea
          ref={taRef}
          className="message-input__textarea"
          rows={3}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onInput={syncTextareaHeight}
          placeholder={placeholder}
          disabled={disabled}
          onKeyDown={handleKeyDown}
        />
      </div>
      <div className="message-input__footer">
        <div className="message-input__footer-left">
          {footerButtonsLeft}
          {foundTags.length > 0 && (
            <span className="mi-hint">{foundTags.length} tag{foundTags.length !== 1 ? 's' : ''} detected</span>
          )}
        </div>
        <div className="message-input__footer-right">
          {footerButtonsRight}
          <button type="button" className="mi-btn primary" disabled={disabled || !hasText} onClick={handleSend}>
            Send <i className="fas fa-paper-plane" />
          </button>
        </div>
      </div>
      {belowFooter ? (
        <div className="message-input__below-footer">
          {belowFooter}
        </div>
      ) : null}
    </div>
  );
}
